// ---------------------------------------------------------------------------
// interface กลางของ AI ในเบราว์เซอร์ (ดัดแปลงจาก AITalk) — หน้าอื่นเรียกผ่านไฟล์นี้เท่านั้น
//  load(webllmId) / generate({system, messages, maxTokens, temperature, onToken}) -> {ok, text, usage:{prompt,completion}, mock?}
// ---------------------------------------------------------------------------
import { IS_MOCK } from "../config";
import { createMockBackend } from "./mockBackend";
import { estimateTokens } from "./tokens";

export const UNSUPPORTED_MSG = "เบราว์เซอร์นี้ยังไม่รองรับ WebGPU จึงรัน AI ในเครื่องไม่ได้ — ลองใช้ Chrome หรือ Edge เวอร์ชันล่าสุดบนคอมพิวเตอร์";
export const NOT_READY_MSG = "ยังไม่ได้โหลดโมเดล — กด \"ดาวน์โหลดโมเดล\" ก่อน (โหลดครั้งเดียว ครั้งต่อไปเปิดจากแคช)";

const initial = () => ({ status: "idle", modelId: "", progress: 0, text: "", error: "" }); // idle|loading|ready|error
let state = initial();
let backend = null;
let current = null;
const subs = new Set();
const set = (patch) => {
  state = { ...state, ...patch };
  subs.forEach((f) => f(state));
};

const makeBackend = async () => {
  if (backend) return backend;
  if (IS_MOCK) {
    backend = createMockBackend();
    return backend;
  }
  backend = (await import("./webllmBackend")).createWebLlmBackend();
  return backend;
};

export const getState = () => state;
export const subscribe = (fn) => {
  subs.add(fn);
  return () => subs.delete(fn);
};
export const isMockEngine = () => IS_MOCK;
export const isSupported = () => IS_MOCK || (typeof navigator !== "undefined" && !!navigator.gpu);
export const isReadyFor = (webllmId) => IS_MOCK || (state.status === "ready" && state.modelId === webllmId);

export async function isCached(webllmId) {
  if (IS_MOCK) return true;
  if (!isSupported()) return false;
  try {
    return await (await makeBackend()).isCached(webllmId);
  } catch (e) {
    return false;
  }
}

/** โหลดโมเดล คืน {ok, message} ไม่ throw */
export async function load(webllmId) {
  if (IS_MOCK) {
    await makeBackend();
    set({ status: "ready", modelId: webllmId, progress: 1, text: "", error: "" });
    return { ok: true };
  }
  if (!isSupported()) {
    set({ status: "error", error: UNSUPPORTED_MSG });
    return { ok: false, message: UNSUPPORTED_MSG };
  }
  if (state.status === "loading") return { ok: false, message: "กำลังโหลดโมเดลอยู่" };
  if (state.status === "ready" && state.modelId === webllmId) return { ok: true };
  set({ status: "loading", modelId: webllmId, progress: 0, text: "เริ่มโหลด...", error: "" });
  try {
    const b = await makeBackend();
    await b.load(webllmId, (p, text) => set({ progress: Math.max(0, Math.min(1, p || 0)), text: text || "" }));
    set({ status: "ready", modelId: webllmId, progress: 1, text: "", error: "" });
    return { ok: true };
  } catch (e) {
    const message = "โหลดโมเดลไม่สำเร็จ: " + ((e && e.message) || "ไม่ทราบสาเหตุ") + " (ลองใหม่ หรือเลือกโมเดลที่เล็กกว่า)";
    set({ status: "error", error: message, progress: 0 });
    return { ok: false, message };
  }
}

/** โหลดอัตโนมัติเฉพาะเมื่อโมเดลอยู่ในแคชแล้ว (ไม่ดาวน์โหลดใหม่เงียบๆ) */
export async function loadIfCached(webllmId) {
  if (IS_MOCK) return (await load(webllmId)).ok;
  if (!isSupported() || state.status === "loading") return false;
  if (state.status === "ready" && state.modelId === webllmId) return true;
  if (!(await isCached(webllmId))) return false;
  return (await load(webllmId)).ok;
}

export function abort() {
  if (!current) return;
  current.cancelled = true;
  if (backend) backend.interrupt();
}

let chain = Promise.resolve();
export function generate(opts = {}) {
  const p = chain.then(() => run(opts));
  chain = p.catch(() => {});
  return p;
}

async function run({ system, messages, maxTokens = 700, temperature = 0.7, onToken } = {}) {
  if (IS_MOCK) await makeBackend();
  if (!isSupported()) return { ok: false, code: "ai_unsupported", message: UNSUPPORTED_MSG };
  if (!IS_MOCK && (state.status !== "ready" || !backend)) return { ok: false, code: "ai_not_ready", message: NOT_READY_MSG };
  const job = { cancelled: false };
  current = job;
  const all = [];
  if (system) all.push({ role: "system", content: system });
  (messages || []).forEach((m) => all.push({ role: m.role === "assistant" ? "assistant" : "user", content: String(m.content || "") }));
  let text = "";
  let usage = null;
  try {
    for await (const tok of backend.stream({ messages: all, maxTokens, temperature, onUsage: (u) => (usage = u) })) {
      if (job.cancelled) break;
      text += tok;
      if (onToken) {
        try {
          onToken(tok, text);
        } catch (e) {
          /* ผู้ฟังพังไม่ควรทำให้คำตอบล้ม */
        }
      }
    }
  } catch (e) {
    if (!job.cancelled) return { ok: false, code: "ai_error", message: "AI ขัดข้อง ลองใหม่อีกครั้ง" };
  } finally {
    if (current === job) current = null;
  }
  text = text.trim();
  // หยุดกลางทาง: ยังคิด token ส่วนที่สร้างไปแล้ว (เก็บคำตอบบางส่วนไว้ได้)
  if (!usage) usage = { prompt: all.reduce((n, m) => n + estimateTokens(m.content), 0), completion: estimateTokens(text) };
  if (!text) return { ok: false, code: job.cancelled ? "ai_cancelled" : "ai_error", message: job.cancelled ? "หยุดการตอบแล้ว" : "AI ไม่ได้ตอบกลับ ลองใหม่อีกครั้ง" };
  return { ok: true, text, usage, stopped: job.cancelled, ...(IS_MOCK ? { mock: true } : {}) };
}
